import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import HolidayCalendar from '../components/HolidayCalendar';
import api from '../api/axios';
import { usePermissions } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';
import ServiceLinesEditor, { ServiceLine, serviceLinePayload } from '../components/ServiceLinesEditor';
import ServiceActsPanel from '../components/ServiceActsPanel';
import ServiceInvoicesPanel from '../components/ServiceInvoicesPanel';

// ===== INTERFACES =====
interface PartyShort { id: number; company_name?: string; contact_person?: string; }

interface ContractVersion {
  id: number;
  contract_id: number;
  version: number;
  title?: string;
  total_amount?: number;
  currency?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  change_comment?: string;
  changed_by?: number;
  changer?: { id: number; full_name: string };
  created_at: string;
}

interface Contract {
  id: number; number: string; contract_type: string; title: string;
  client?: PartyShort; supplier?: PartyShort;
  currency: string; total_amount: number; transport_cost?: number; status: string;
  prepayment_percent?: number; delivery_days?: number; prepayment_days?: number;
  penalty_delivery?: number; penalty_delivery_cap?: number; penalty_acceptance?: number;
  penalty_payment?: number; penalty_payment_cap?: number;
  vat_percent?: number; delivery_method?: string;
  iso_standard_option?: string; iso_standard_other_text?: string;
  final_payment_days?: number; service_equipment?: EquipmentRow[] | null;
  execution_terms?: string | null;
  service_items?: ServiceLine[] | null; external_contract_number?: string | null; contract_date?: string | null;
  start_date?: string; end_date?: string; description?: string;
  file_url?: string;
  created_by?: number;
  creator?: { id: number; full_name: string };
  assigned_to_id?: number;
  assignee?: { id: number; full_name: string };
  quotation_id?: number;
  versions?: ContractVersion[];
  created_at: string;
}

interface AssignableUser {
  id: number;
  full_name: string;
  role: string;
  role_label?: string;
}

const ROLE_GROUP_ORDER = [
  'admin', 'director', 'sales_head', 'sales_manager',
  'service_head', 'service_engineer', 'warehouse_head',
  'logistic_head', 'logist', 'chief_accountant', 'accountant',
  'hr_manager', 'lawyer',
];

interface InvoiceItem {
  id: number;
  product_id?: number;
  product_name: string;
  product_code?: string;
  unit?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

interface Invoice {
  id: number; number: string; contract_id?: number;
  client?: PartyShort; supplier?: PartyShort;
  invoice_type: string; currency: string;
  amount: number; paid_amount: number; status: string;
  due_date?: string; description?: string;
  file_url?: string; created_at: string;
  items?: InvoiceItem[];
}

interface Transaction {
  id: number; transaction_type: string;
  client?: PartyShort; supplier?: PartyShort;
  invoice_id?: number; currency: string; amount: number;
  description?: string; transaction_date: string;
}

interface Expense {
  id: number; title: string; category: string;
  currency: string; amount: number;
  description?: string; expense_date: string;
}

interface Summary {
  income_usd: number; income_uzs: number;
  expense_usd: number; expense_uzs: number;
  profit_usd: number; profit_uzs: number;
  unpaid_invoices: number; active_contracts: number;
}

interface Client { id: number; company_name?: string; contact_person?: string; }
interface Supplier { id: number; company_name: string; }

// ===== CONFIGS =====
const currencySymbol: Record<string, string> = { usd: '$', eur: '€', uzs: "so'm" };

const contractTypeConfig: Record<string, { label: string; color: string; icon: string }> = {
  client:   { label: 'Yetkazib berish', color: 'bg-blue-100 text-blue-700',    icon: '📦' },
  supplier: { label: 'Xarid',           color: 'bg-purple-100 text-purple-700', icon: '🛒' },
  service:  { label: 'Xizmat',          color: 'bg-green-100 text-green-700',   icon: '🔧' },
};

const invoiceTypeConfig: Record<string, { label: string; color: string; icon: string }> = {
  client:   { label: 'Yetkazib berish', color: 'bg-blue-100 text-blue-700',    icon: '📦' },
  supplier: { label: 'Xarid',           color: 'bg-purple-100 text-purple-700', icon: '🛒' },
  service:  { label: 'Xizmat',          color: 'bg-green-100 text-green-700',   icon: '🔧' },
};

const contractStatusConfig: Record<string, { label: string; color: string }> = {
  draft:     { label: 'Qoralama',  color: 'bg-gray-100 text-gray-700' },
  active:    { label: 'Faol',      color: 'bg-green-100 text-green-700' },
  completed: { label: 'Bajarildi', color: 'bg-blue-100 text-blue-700' },
  cancelled: { label: 'Bekor',     color: 'bg-red-100 text-red-700' },
};

// Shartnoma matni shartlari (to'liq matnli shartnoma STAGE 2'da shu qiymatlarni
// o'qiydi - qarang backend/app/core/contract_text.py CONTRACT_TERM_DEFAULTS,
// ikkala tarafda bir xil standart qiymatlar bo'lishi kerak).
const CONTRACT_TERM_DEFAULTS = {
  prepayment_percent: 100,
  prepayment_days: 5,
  delivery_days: 10,
  penalty_delivery: 0.5,
  penalty_delivery_cap: 50,
  penalty_acceptance: 10,
  penalty_payment: 0.4,
  penalty_payment_cap: 50,
  vat_percent: 12,
};

const CONTRACT_TERM_FIELDS: { key: keyof typeof CONTRACT_TERM_DEFAULTS; label: string; step: number }[] = [
  { key: 'prepayment_percent',   label: "Oldindan to'lov (%)",                     step: 1 },
  { key: 'prepayment_days',      label: "Oldindan to'lov muddati (bank kuni)",     step: 1 },
  { key: 'delivery_days',        label: 'Yetkazib berish muddati (kalendar kun)',  step: 1 },
  { key: 'penalty_delivery',     label: 'Kechiktirilgan yetkazib berish penyasi (%/kun)', step: 0.1 },
  { key: 'penalty_delivery_cap', label: 'Yetkazib berish penyasi yuqori chegarasi (%)',   step: 1 },
  { key: 'penalty_acceptance',   label: "Qabul qilishdan bosh tortish jarimasi (%)", step: 1 },
  { key: 'penalty_payment',      label: "Kechiktirilgan to'lov penyasi (%/kun)",   step: 0.1 },
  { key: 'penalty_payment_cap',  label: "To'lov penyasi yuqori chegarasi (%)",     step: 1 },
  { key: 'vat_percent',          label: 'QQS foizi (%)',                          step: 1 },
];

// Band 3.3 - yetkazib berish usuli tanlovi (qarang backend/app/core/
// contract_text.py DELIVERY_METHOD_TEXT - qiymatlar shu bilan bir xil
// bo'lishi shart).
const DELIVERY_METHOD_DEFAULT = 'self_pickup';
const DELIVERY_METHOD_OPTIONS: { value: string; label: string }[] = [
  { value: 'self_pickup', label: "Mustaqil olib ketish (Samovyvoz)" },
  { value: 'included',    label: "Yetkazib berish xizmati qo'shilgan" },
];

// Xarid (supplier) shartnomasi band 4.1 - sifat standarti tanlovi (qarang
// backend/app/core/contract_text.py ISO_STANDARD_OPTIONS - qiymatlar shu
// bilan bir xil bo'lishi shart).
const ISO_STANDARD_DEFAULT = 'reference_materials';
const ISO_STANDARD_OPTIONS: { value: string; label: string }[] = [
  { value: 'reference_materials', label: 'Standart namunalar / reaktivlar (ISO 17034 + ISO 17025)' },
  { value: 'instruments',         label: 'Asboblar (ISO 9001)' },
  { value: 'other',               label: "Boshqa (qo'lda kiritiladi)" },
];

// delivery_days/prepayment_days - sotuv va xarid shartnomalarida BIR XIL
// Contract ustunlaridan foydalanadi, lekin standart qiymatlari va matn
// ma'nosi turlicha (sotuv: kalendar kun/bank kuni; xarid: ish kuni/kun -
// qarang backend PURCHASE_TERM_DEFAULTS). Forma shu farqni aks ettiradi.
const PURCHASE_TERM_DEFAULTS: { delivery_days: number; prepayment_days: number } = {
  delivery_days: 20,
  prepayment_days: 15,
};
const PURCHASE_TERM_LABELS: Partial<Record<keyof typeof CONTRACT_TERM_DEFAULTS, string>> = {
  delivery_days: 'Yetkazib berish muddati (ish kuni)',
  prepayment_days: "Oldindan to'lov muddati (kun)",
};
// Mavjud shartnomani tahrirlashda delivery_days/prepayment_days bo'sh
// (null) bo'lsa - qaysi standart qiymat ko'rsatilishi kerakligini shartnoma
// turiga qarab hal qiladi (xarid: PURCHASE_TERM_DEFAULTS, boshqalar:
// CONTRACT_TERM_DEFAULTS) - backend render vaqtida xuddi shu qoidani
// qo'llaydi (contract_text.py resolve_purchase_terms vs resolve_contract_terms).
const getTermDefault = (key: keyof typeof CONTRACT_TERM_DEFAULTS, contractType: string) => {
  if (contractType === 'supplier' && key in PURCHASE_TERM_DEFAULTS) {
    return (PURCHASE_TERM_DEFAULTS as any)[key];
  }
  if (contractType === 'service' && key in SERVICE_TERM_DEFAULTS) {
    return (SERVICE_TERM_DEFAULTS as any)[key];
  }
  return CONTRACT_TERM_DEFAULTS[key];
};

// Xizmat (servis) shartnomasi - qarang backend/app/core/service_contract_text.py
// SERVICE_TERM_DEFAULTS (qiymatlar bir xil bo'lishi shart).
const SERVICE_TERM_DEFAULTS = { prepayment_percent: 50, prepayment_days: 3, final_payment_days: 5 };
// Xizmat shartnomasida faqat shu shartlar matnda ishlatiladi (qolganlari - sotuv shartnomasiniki).
// QQS stavkasi xizmat shartnomasida kiritilmaydi - yagona manba kompaniya sozlamasi
// (qoralama - joriy sozlama, berilganda snapshot; backend service_billing.document_vat_rate).
const SERVICE_TERM_KEYS: (keyof typeof CONTRACT_TERM_DEFAULTS)[] = ['prepayment_percent', 'prepayment_days'];

// Ilova №1 - uskunalar ro'yxati. category -> 4.3-banddagi kafolat muddati.
interface EquipmentRow { name: string; serial?: string | null; category: string | null; }
const EQUIPMENT_CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'heating',            label: 'Elektr isitish (kafolat 1 oy)' },
  { value: 'electromechanical',  label: 'Elektromexanik (kafolat 2 oy)' },
  { value: 'electronic_optical', label: 'Elektron / optik (kafolat 3 oy)' },
];
interface InstrumentOption { id: number; model: string; manufacturer?: string; serial_number?: string; inventory_number?: string; }

const invoiceStatusConfig: Record<string, { label: string; color: string }> = {
  draft:     { label: 'Qoralama',      color: 'bg-gray-100 text-gray-700' },
  sent:      { label: 'Yuborildi',     color: 'bg-blue-100 text-blue-700' },
  paid:      { label: "To'landi",      color: 'bg-green-100 text-green-700' },
  overdue:   { label: "Muddati o'tdi", color: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Bekor',         color: 'bg-gray-100 text-gray-500' },
};

const expenseCategoryConfig: Record<string, { label: string; icon: string }> = {
  salary:    { label: 'Maosh',     icon: '👤' },
  rent:      { label: 'Ijara',     icon: '🏢' },
  transport: { label: 'Transport', icon: '🚗' },
  office:    { label: 'Ofis',      icon: '📎' },
  marketing: { label: 'Marketing', icon: '📣' },
  tax:       { label: 'Soliq',     icon: '📋' },
  other:     { label: 'Boshqa',    icon: '💡' },
};

type TabType = 'summary' | 'supply' | 'purchase' | 'service' |
               'inv_supply' | 'inv_purchase' | 'inv_service' |
               'transactions' | 'expenses' | 'calendar';
type ContractTabType = 'supply' | 'purchase' | 'service';
type InvoiceTabType = 'inv_supply' | 'inv_purchase' | 'inv_service';

const contractTabMap: Record<ContractTabType, string> = {
  supply: 'client', purchase: 'supplier', service: 'service',
};

const invoiceTabMap: Record<InvoiceTabType, string> = {
  inv_supply: 'client', inv_purchase: 'supplier', inv_service: 'service',
};

const Accounting: React.FC = () => {
  const { canEdit, canView } = usePermissions();
  const { user } = useAuth();
  const canFull = canEdit('accounting');
  const location = useLocation();
  const urlTab = new URLSearchParams(location.search).get('tab') as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(urlTab || 'summary');

  // sales_head/service_head - FAQAT o'z bo'limining shartnomasi (Invoice/
  // Transaction/Expense/Xulosa - buxgalteriya bo'limining o'zi, ular uchun
  // emas - qarang backend/app/routers/accounting.py require_accounting_view).
  // "department"/"access_level" - backend/app/core/access_scope.py'dan
  // (/auth/me), faqat qaysi tabni ko'rsatishni tanlash uchun - haqiqiy
  // ruxsat baribir har bir so'rovda backendda tekshiriladi.
  const restrictedDept = (user?.access_level !== 'company' && (user?.department === 'sales' || user?.department === 'service'))
    ? user!.department as 'sales' | 'service'
    : null;

  useEffect(() => {
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [location.search]);

  useEffect(() => {
    api.get('/company-settings').then(res => {
      // YaTT (legal_form=sole_proprietor): standart shartnoma turi - "Xizmat"
      // (backend from-quotation bilan bir xil qoida).
      if (res.data?.legal_form === 'sole_proprietor' && !urlTab && !restrictedDept) setActiveTab('service');
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!restrictedDept) return;
    const allowedTab: TabType = restrictedDept === 'sales' ? 'supply' : 'service';
    setActiveTab(prev => (prev === allowedTab ? prev : allowedTab));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictedDept]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);

  const [showModal, setShowModal] = useState<string | null>(null);
  const [showExportModal, setShowExportModal] = useState<number | null>(null);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState<Invoice | null>(null);
  const [editItem, setEditItem] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [error, setError] = useState('');

  const [exportLang, setExportLang] = useState('uz');
  const [exportFormat, setExportFormat] = useState('pdf');

  const [contractForm, setContractForm] = useState({
    contract_type: 'client', title: '', client_id: '', supplier_id: '',
    currency: 'usd', total_amount: 0, transport_cost: 0, status: 'draft',
    start_date: '', end_date: '', description: '', assigned_to_id: '',
    delivery_method: DELIVERY_METHOD_DEFAULT,
    iso_standard_option: ISO_STANDARD_DEFAULT, iso_standard_other_text: '',
    ...CONTRACT_TERM_DEFAULTS,
    final_payment_days: SERVICE_TERM_DEFAULTS.final_payment_days,
    execution_terms: '',
    service_equipment: [] as EquipmentRow[],
    service_items: [] as ServiceLine[],
    external_contract_number: '', contract_date: '',
  });
  // Ilova №1 uchun mijoz uskunalari (Instruments moduli). null = ruxsat yo'q
  // yoki yuklanmagan -> tanlash tugmasi ko'rsatilmaydi (qo'lda kiritish asosiy yo'l).
  const [clientInstruments, setClientInstruments] = useState<InstrumentOption[] | null>(null);
  // Xarid shartnomasi uchun: tovarlar summasi alohida saqlanadi, total_amount = goods + transport
  const [goodsAmount, setGoodsAmount] = useState(0);
  // "Qo'shimcha shartlar" bo'limi - forma bilan tiqilib qolmasligi uchun
  // sukut bo'yicha yopiq (App.tsx MobileSidebar expandedGroup konvensiyasiga mos).
  const [showAdvancedTerms, setShowAdvancedTerms] = useState(false);

  const [invoiceForm, setInvoiceForm] = useState({
    invoice_type: 'client', client_id: '', supplier_id: '',
    contract_id: '', currency: 'usd', amount: 0,
    due_date: '', description: '',
  });

  const [transactionForm, setTransactionForm] = useState({
    transaction_type: 'income', invoice_id: '', client_id: '',
    supplier_id: '', currency: 'usd', amount: 0, description: '',
    transaction_date: '',
  });

  const [expenseForm, setExpenseForm] = useState({
    title: '', category: 'other', currency: 'uzs',
    amount: 0, description: '', expense_date: '',
  });

  const fetchAll = async () => {
    try { const r = await api.get('/contracts'); setContracts(r.data); } catch (e) { setContracts([]); }
    try { const r = await api.get('/invoices'); setInvoices(r.data); } catch (e) { setInvoices([]); }
    try { const r = await api.get('/transactions'); setTransactions(r.data); } catch (e) { setTransactions([]); }
    try { const r = await api.get('/expenses'); setExpenses(r.data); } catch (e) { setExpenses([]); }
    try { const r = await api.get('/accounting/summary'); setSummary(r.data); } catch (e) { setSummary(null); }
    try { const r = await api.get('/clients'); setClients(r.data); } catch (e) { setClients([]); }
    try { const r = await api.get('/suppliers'); setSuppliers(r.data); } catch (e) { setSuppliers([]); }
    try { const r = await api.get('/users/assignable'); setAssignableUsers(r.data); } catch (e) { setAssignableUsers([]); }
  };

  useEffect(() => { fetchAll(); }, []);
  // Xarid shartnomasi ikki tilli (chap ustun uz/ru, o'ng ustun doim ingliz) - "en" varianti yo'q.
  const exportIsPurchase = contracts.find(c => c.id === showExportModal)?.contract_type === 'supplier';
  useEffect(() => { if (exportIsPurchase && exportLang === 'en') setExportLang('uz'); }, [exportIsPurchase, exportLang]);


  const handleCreateInvoice = async (contractId: number, contractNumber: string) => {
    if (!window.confirm(`${contractNumber} asosida invoys yaratilasinmi?`)) return;
    try {
      const res = await api.post(`/invoices/from-contract/${contractId}`);
      const invNumber = res.data.number;
      const invType = res.data.invoice_type;
      const invTab = invType === 'supplier' ? 'inv_purchase' : invType === 'service' ? 'inv_service' : 'inv_supply';
      await fetchAll();
      setActiveTab(invTab as any);
      alert(`✅ Invoys yaratildi: ${invNumber}\nInvoyslar bo'limida ko'ring.`);
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      alert(Array.isArray(detail) ? detail.map((d: any) => d.msg).join('\n') : detail || 'Xato yuz berdi');
    }
  };

  const handleCreateTransaction = async (invoiceId: number, invoiceNumber: string) => {
    if (!window.confirm(`${invoiceNumber} asosida to'lov yaratilasinmi?`)) return;
    try {
      const res = await api.post(`/transactions/from-invoice/${invoiceId}`);
      const txAmount = res.data.amount;
      await fetchAll();
      setActiveTab('transactions');
      alert(`✅ To'lov yaratildi: ${txAmount.toLocaleString()}\nTransaksiyalar bo'limida ko'ring.`);
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      alert(Array.isArray(detail) ? detail.map((d: any) => d.msg).join('\n') : detail || 'Xato yuz berdi');
    }
  };

  const getPartyName = (item: any) => {
    if (item?.client) return item.client.company_name || item.client.contact_person;
    if (item?.supplier) return item.supplier.company_name;
    return '—';
  };

  const getContractsByType = (type: ContractTabType) =>
    contracts.filter(c => c.contract_type === contractTabMap[type]);

  const getInvoicesByType = (type: InvoiceTabType) =>
    invoices.filter(i => i.invoice_type === invoiceTabMap[type]);

  const handleContractExport = async (contractId: number, number: string, contractType: string) => {
    const exportType = contractType === 'client' ? 'supply' : contractType === 'supplier' ? 'purchase' : 'service';
    // Xizmat shartnomasi: end_date (10.1-band) yoki bajarish muddati (3.1-band) bo'sh bo'lsa ogohlantirish
    const exported = contracts.find(c => c.id === contractId);
    if (exportType === 'service') {
      const missing: string[] = [];
      if (!exported?.end_date) missing.push("amal qilish muddati (tugash sanasi) kiritilmagan - 10.1-bandida bo'sh chiziq chiqadi");
      if (!(exported?.execution_terms || '').trim()) missing.push("ishlarni bajarish muddati kiritilmagan - 3.1-bandda umumiy jumla chiqadi (\"xizmatlar ko'rsatish tugallangan paytgacha\")");
      if (missing.length && !window.confirm(`Shartnomada: ${missing.join('; ')}. Baribir yuklab olasizmi?`)) return;
    }
    try {
      const url = `/contracts/${contractId}/export/${exportFormat}?contract_type=${exportType}&lang=${exportLang}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${number}_${exportType}_${exportLang}.${exportFormat}`;
      a.click();
      setShowExportModal(null);
    } catch (e) { alert('Fayl yaratishda xato'); }
  };

  const handleInvoicePdf = async (id: number, number: string) => {
    try {
      const res = await api.get(`/invoices/${id}/export/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${number}.pdf`; a.click();
    } catch (e) { alert('PDF yaratishda xato'); }
  };

  const handleFileUpload = async (invoiceId: number, file: File) => {
    if (!canFull) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/invoices/${invoiceId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchAll();
      const res = await api.get(`/invoices/${invoiceId}`);
      setShowInvoiceDetail(res.data);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Fayl yuklashda xato');
    } finally { setUploadingFile(false); }
  };

  const handleFileDelete = async (invoiceId: number) => {
    if (!canFull) return;
    if (!window.confirm("Faylni o'chirishni tasdiqlaysizmi?")) return;
    await api.delete(`/invoices/${invoiceId}/upload`);
    fetchAll();
    const res = await api.get(`/invoices/${invoiceId}`);
    setShowInvoiceDetail(res.data);
  };

  // FIX #7: fayl endi /uploads static mountda emas - login + ruxsat
  // tekshiruvidan o'tgan `api` instansiyasi orqali (Authorization header
  // bilan) yuklab olinadi, keyin vaqtinchalik blob havolasi ochiladi.
  const handleFileView = async (invoiceId: number) => {
    try {
      const res = await api.get(`/invoices/${invoiceId}/file`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Faylni ochishda xato');
    }
  };

  const handleContractSubmit = async () => {
    if (!canFull) return;
    if (!contractForm.title) { setError("Sarlavha kiritilishi kerak"); return; }
    setLoading(true); setError('');
    try {
      const payload = {
        ...contractForm,
        client_id: contractForm.client_id ? Number(contractForm.client_id) : null,
        supplier_id: contractForm.supplier_id ? Number(contractForm.supplier_id) : null,
        assigned_to_id: contractForm.assigned_to_id ? Number(contractForm.assigned_to_id) : null,
        total_amount: contractForm.contract_type === 'supplier'
          ? Number(goodsAmount) + Number(contractForm.transport_cost || 0)
          : Number(contractForm.total_amount),
        transport_cost: contractForm.contract_type === 'supplier' ? Number(contractForm.transport_cost || 0) : 0,
        start_date: contractForm.start_date ? new Date(contractForm.start_date).toISOString() : null,
        end_date: contractForm.end_date ? new Date(contractForm.end_date).toISOString() : null,
        ...Object.fromEntries(CONTRACT_TERM_FIELDS.map(f => [f.key, Number(contractForm[f.key])])),
        // Xizmat shartnomasi maydonlari faqat shu turda yuboriladi
        final_payment_days: contractForm.contract_type === 'service' ? Number(contractForm.final_payment_days) : undefined,
        execution_terms: contractForm.contract_type === 'service' ? (contractForm.execution_terms.trim() || null) : undefined,
        service_equipment: contractForm.contract_type === 'service'
          ? contractForm.service_equipment
              .filter(r => r.name.trim())
              .map(r => ({ name: r.name.trim(), serial: (r.serial || '').trim() || null, category: r.category || null }))
          : undefined,
        // Ilova №2 qatorlari (taklifdan nusxa, tahrirlanadi). Bo'sh bo'lsa null -
        // eski shartnomalardagidek taklif qatorlari ishlatiladi.
        service_items: contractForm.contract_type === 'service'
          ? (contractForm.service_items.length ? contractForm.service_items.map(serviceLinePayload) : null)
          : undefined,
        external_contract_number: contractForm.external_contract_number || null,
        // (H1) bo'sh - server bugungi sanani (Toshkent) qo'yadi; tahrirda bo'sh yuborilmaydi
        ...(contractForm.contract_type === 'service' && contractForm.contract_date ? { contract_date: contractForm.contract_date } : {}),
      };
      if (editItem) await api.put(`/contracts/${editItem.id}`, payload);
      else await api.post('/contracts', payload);
      setShowModal(null); setEditItem(null); fetchAll();
    } catch (e: any) { setError(e.response?.data?.detail || 'Xato'); }
    finally { setLoading(false); }
  };

  const handleInvoiceSubmit = async () => {
    if (!canFull) return;
    setLoading(true); setError('');
    try {
      const payload = {
        ...invoiceForm,
        client_id: invoiceForm.client_id ? Number(invoiceForm.client_id) : null,
        supplier_id: invoiceForm.supplier_id ? Number(invoiceForm.supplier_id) : null,
        contract_id: invoiceForm.contract_id ? Number(invoiceForm.contract_id) : null,
        amount: Number(invoiceForm.amount),
        due_date: invoiceForm.due_date ? new Date(invoiceForm.due_date).toISOString() : null,
      };
      if (editItem) await api.put(`/invoices/${editItem.id}`, payload);
      else await api.post('/invoices', payload);
      setShowModal(null); setEditItem(null); fetchAll();
    } catch (e: any) { setError(e.response?.data?.detail || 'Xato'); }
    finally { setLoading(false); }
  };

  const handleTransactionSubmit = async () => {
    if (!canFull) return;
    setLoading(true); setError('');
    try {
      const payload = {
        ...transactionForm,
        invoice_id: transactionForm.invoice_id ? Number(transactionForm.invoice_id) : null,
        client_id: transactionForm.client_id ? Number(transactionForm.client_id) : null,
        supplier_id: transactionForm.supplier_id ? Number(transactionForm.supplier_id) : null,
        amount: Number(transactionForm.amount),
        transaction_date: transactionForm.transaction_date
          ? new Date(transactionForm.transaction_date).toISOString() : null,
      };
      if (editItem) await api.put(`/transactions/${editItem.id}`, payload);
      else await api.post('/transactions', payload);
      setShowModal(null); setEditItem(null); fetchAll();
    } catch (e: any) { setError(e.response?.data?.detail || 'Xato'); }
    finally { setLoading(false); }
  };

  const handleExpenseSubmit = async () => {
    if (!canFull) return;
    if (!expenseForm.title) { setError("Sarlavha kiritilishi kerak"); return; }
    setLoading(true); setError('');
    try {
      const payload = {
        ...expenseForm,
        amount: Number(expenseForm.amount),
        expense_date: expenseForm.expense_date
          ? new Date(expenseForm.expense_date).toISOString() : null,
      };
      if (editItem) await api.put(`/expenses/${editItem.id}`, payload);
      else await api.post('/expenses', payload);
      setShowModal(null); setEditItem(null); fetchAll();
    } catch (e: any) { setError(e.response?.data?.detail || 'Xato'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (type: string, id: number) => {
    if (!canFull) return;
    if (!window.confirm("O'chirishni tasdiqlaysizmi?")) return;
    await api.delete(`/${type}/${id}`);
    fetchAll();
  };

  const openContractModal = (contractType: string) => {
    setEditItem(null);
    setContractForm({
      contract_type: contractType, title: '', client_id: '', supplier_id: '',
      currency: 'usd', total_amount: 0, transport_cost: 0, status: 'draft',
      start_date: '', end_date: '', description: '',
      delivery_method: DELIVERY_METHOD_DEFAULT,
      iso_standard_option: ISO_STANDARD_DEFAULT, iso_standard_other_text: '',
      ...CONTRACT_TERM_DEFAULTS,
      ...(contractType === 'supplier' ? PURCHASE_TERM_DEFAULTS : {}),
      ...(contractType === 'service' ? SERVICE_TERM_DEFAULTS : {}),
      final_payment_days: SERVICE_TERM_DEFAULTS.final_payment_days,
      execution_terms: '',
      service_equipment: [],
      service_items: [],
      external_contract_number: '', contract_date: '',
    } as any);
    setClientInstruments(null);
    setGoodsAmount(0);
    setShowAdvancedTerms(false);
    setError(''); setShowModal('contract');
  };

  const openInvoiceModal = (invoiceType: string) => {
    setEditItem(null);
    setInvoiceForm({
      invoice_type: invoiceType, client_id: '', supplier_id: '',
      contract_id: '', currency: 'usd', amount: 0, due_date: '', description: '',
    });
    setError(''); setShowModal('invoice');
  };

  // ===== INVOICE JADVALI =====
  const InvoiceTable: React.FC<{ invoiceTab: InvoiceTabType }> = ({ invoiceTab }) => {
    const filtered = getInvoicesByType(invoiceTab);
    const cfg = invoiceTypeConfig[invoiceTabMap[invoiceTab]];
    const isClientType = invoiceTab !== 'inv_purchase';

    return (
      <div>
        <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
          <div className="flex gap-2 flex-wrap">
            {Object.entries(invoiceStatusConfig).map(([k, v]) => (
              <div key={k} className="bg-white rounded-xl shadow px-2 md:px-3 py-2 text-xs">
                <span className={`px-2 py-0.5 rounded-full whitespace-nowrap ${v.color}`}>{v.label}</span>
                <span className="ml-1 font-bold">{filtered.filter(i => i.status === k).length}</span>
              </div>
            ))}
          </div>
          <button onClick={() => canFull && openInvoiceModal(invoiceTabMap[invoiceTab])} disabled={!canFull}
            title={!canFull ? "Faqat ko'rish huquqi" : undefined}
            className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>
            + Yangi {cfg.label.toLowerCase()} invoice
          </button>
        </div>

        <div className="bg-white rounded-xl shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-2 md:px-4 py-3 text-left">Raqam / {isClientType ? 'Mijoz' : 'Yetkazib beruvchi'}</th>
                <th className="px-2 md:px-4 py-3 text-right">Summa</th>
                <th className="px-2 md:px-4 py-3 text-right hidden sm:table-cell">To'landi</th>
                <th className="px-2 md:px-4 py-3 text-right hidden sm:table-cell">Qoldi</th>
                <th className="px-2 md:px-4 py-3 text-left">Holat</th>
                <th className="px-2 md:px-4 py-3 text-left hidden lg:table-cell">Muddat</th>
                <th className="px-2 md:px-4 py-3 text-center hidden md:table-cell">Fayl</th>
                <th className="px-2 md:px-4 py-3 text-center">Amallar</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10 text-gray-400">
                  {cfg.icon} {cfg.label} invoicelar yo'q
                </td></tr>
              ) : filtered.map(inv => (
                <tr key={inv.id} className={`border-t hover:bg-gray-50 ${inv.status === 'overdue' ? 'bg-red-50' : ''}`}>
                  <td className="px-2 md:px-4 py-3">
                    <div className="font-mono text-blue-600 font-medium text-xs">{inv.number}</div>
                    <div className="text-gray-600 text-sm">{getPartyName(inv)}</div>
                    <div className="sm:hidden text-xs text-gray-400 mt-0.5">
                      To'landi: <span className="text-green-600">{inv.paid_amount.toLocaleString()}</span> / Qoldi: <span className="text-red-500">{(inv.amount - inv.paid_amount).toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="px-2 md:px-4 py-3 text-right font-medium whitespace-nowrap">
                    {inv.amount.toLocaleString()} {currencySymbol[inv.currency]}
                  </td>
                  <td className="px-2 md:px-4 py-3 text-right text-green-600 hidden sm:table-cell whitespace-nowrap">
                    {inv.paid_amount.toLocaleString()} {currencySymbol[inv.currency]}
                  </td>
                  <td className="px-2 md:px-4 py-3 text-right text-red-500 hidden sm:table-cell whitespace-nowrap">
                    {(inv.amount - inv.paid_amount).toLocaleString()} {currencySymbol[inv.currency]}
                  </td>
                  <td className="px-2 md:px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs whitespace-nowrap ${invoiceStatusConfig[inv.status]?.color}`}>
                      {invoiceStatusConfig[inv.status]?.label}
                    </span>
                  </td>
                  <td className="px-2 md:px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">
                    {inv.due_date ? new Date(inv.due_date).toLocaleDateString('ru-RU') : '—'}
                  </td>
                  <td className="px-2 md:px-4 py-3 text-center hidden md:table-cell">
                    {inv.file_url ? (
                      <span className="text-green-600 text-xs font-medium">📎 Bor</span>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-2 md:px-4 py-3">
                    <div className="flex justify-center gap-1">
                      <button onClick={() => setShowInvoiceDetail(inv)} title="Ko'rish"
                        className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs">👁️</button>
                      <button onClick={() => handleInvoicePdf(inv.id, inv.number)} title="PDF yuklab olish"
                        className="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs">📄</button>
                      <button onClick={() => {
                        if (!canFull) return;
                        setEditItem(inv);
                        setInvoiceForm({
                          invoice_type: inv.invoice_type,
                          client_id: inv.client?.id?.toString() || '',
                          supplier_id: inv.supplier?.id?.toString() || '',
                          contract_id: inv.contract_id?.toString() || '',
                          currency: inv.currency, amount: inv.amount,
                          due_date: inv.due_date ? inv.due_date.split('T')[0] : '',
                          description: inv.description || '',
                        });
                        setError(''); setShowModal('invoice');
                      }} disabled={!canFull}
                        title={!canFull ? "Faqat ko'rish huquqi" : "Tahrirlash"}
                        className={canFull ? "px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>✏️</button>
                      <button onClick={() => handleDelete('invoices', inv.id)} disabled={!canFull}
                        title={!canFull ? "Faqat ko'rish huquqi" : "O'chirish"}
                        className={canFull ? "px-2 py-1 bg-red-100 text-red-700 rounded text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ===== SHARTNOMA JADVALI =====
  const ContractTable: React.FC<{ contractTab: ContractTabType }> = ({ contractTab }) => {
  const filtered = getContractsByType(contractTab);
  const cfg = contractTypeConfig[contractTabMap[contractTab]];
  const isClientType = contractTab !== 'purchase';
  const [showDetail, setShowDetail] = useState<Contract | null>(null);
  const [contractVersions, setContractVersions] = useState<ContractVersion[]>([]);
  const [showVersions, setShowVersions] = useState(false);
  const [versionComment, setVersionComment] = useState('');
  const [addingVersion, setAddingVersion] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Dashboard "So'nggi faoliyat" feed havolasi ?tab=...&open=<id> bilan
  // keladi - "tab" allaqachon tashqi Accounting komponentida o'qilib
  // activeTab'ni belgilaydi, shu TAB'ga mos ContractTable mount bo'lganda
  // "open"ni o'qib xuddi shu shartnomani detal oynasida ochamiz. Ruxsat
  // bo'lmasa (403) - jim o'tkazib yuboriladi.
  useEffect(() => {
    const openId = new URLSearchParams(location.search).get('open');
    if (openId) {
      api.get(`/contracts/${openId}`).then(res => setShowDetail(res.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dashboard status kartalari ("Faol shartnomalar" / "30 kun ichida
  // tugaydigan shartnomalar") ?status=active / ?expiring=<kun> bilan keladi -
  // backend/app/routers/dashboard.py'dagi _contract_summary() bilan BIR XIL
  // qoida (expiring = status active VA end_date [bugun, bugun+N kun]
  // oralig'ida) - raqam bilan ro'yxat mos kelishi uchun. Faqat RO'YXATNI
  // toraytiradi - yuqoridagi "Jami"/"Faol" belgilar butun tab bo'yicha
  // qoladi (foydalanuvchi filtr faolligini va umumiy kontekstni ko'rishi uchun).
  const urlParams = new URLSearchParams(location.search);
  const statusFilter = urlParams.get('status');
  const expiringDays = urlParams.get('expiring');
  const isEndDateWithinDays = (endDate: string | undefined | null, days: number) => {
    if (!endDate) return false;
    const end = new Date(endDate);
    const now = new Date();
    const horizon = new Date();
    horizon.setDate(horizon.getDate() + days);
    return end >= now && end <= horizon;
  };
  const visibleContracts = filtered.filter(c => {
    if (statusFilter && c.status !== statusFilter) return false;
    if (expiringDays && (c.status !== 'active' || !isEndDateWithinDays(c.end_date, Number(expiringDays)))) return false;
    return true;
  });
  const hasActiveFilter = Boolean(statusFilter || expiringDays);

  const loadVersions = async (contractId: number) => {
    try {
      const res = await api.get(`/contracts/${contractId}/versions`);
      setContractVersions(res.data);
    } catch (e) { setContractVersions([]); }
  };

  const handleAddVersion = async (contractId: number) => {
    if (!versionComment.trim()) return;
    setAddingVersion(true);
    try {
      await api.post(`/contracts/${contractId}/versions`, { change_comment: versionComment });
      setVersionComment('');
      await loadVersions(contractId);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Xato');
    } finally { setAddingVersion(false); }
  };

  const handleVersionExport = async (contractId: number, versionId: number, versionNum: number, format: string, contractType: string) => {
    const exportType = contractType === 'client' ? 'supply' : contractType === 'supplier' ? 'purchase' : 'service';
    const versionLang = exportType === 'purchase' && exportLang === 'en' ? 'uz' : exportLang;
    try {
      const res = await api.get(
        `/contracts/${contractId}/versions/${versionId}/export/${format}?contract_type=${exportType}&lang=${versionLang}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `v${versionNum}_${exportType}_${versionLang}.${format}`;
      a.click();
    } catch (e) { alert('Eksport xatosi'); }
  };

  const handleFileUpload = async (contractId: number, file: File) => {
    if (!canFull) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/contracts/${contractId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchAll();
      const res = await api.get(`/contracts/${contractId}`);
      setShowDetail(res.data);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Fayl yuklashda xato');
    } finally { setUploadingFile(false); }
  };

  const handleFileDelete = async (contractId: number) => {
    if (!canFull) return;
    if (!window.confirm("Faylni o'chirishni tasdiqlaysizmi?")) return;
    await api.delete(`/contracts/${contractId}/upload`);
    fetchAll();
    const res = await api.get(`/contracts/${contractId}`);
    setShowDetail(res.data);
  };

  // FIX #7: qarang invoice handleFileView'dagi izoh - xuddi shu naqsh.
  const handleFileView = async (contractId: number) => {
    try {
      const res = await api.get(`/contracts/${contractId}/file`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Faylni ochishda xato');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
        <div className="flex gap-2 md:gap-3">
          <div className="bg-white rounded-xl shadow px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm">
            Jami: <b>{filtered.length}</b>
          </div>
          <div className="bg-white rounded-xl shadow px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm">
            Faol: <b className="text-green-600">{filtered.filter(c => c.status === 'active').length}</b>
          </div>
        </div>
        <button onClick={() => canFull && openContractModal(contractTabMap[contractTab])} disabled={!canFull}
          title={!canFull ? "Faqat ko'rish huquqi" : undefined}
          className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>
          + Yangi {cfg.label.toLowerCase()} shartnomasi
        </button>
      </div>

      {hasActiveFilter && (
        <div className="flex items-center justify-between gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-3 text-xs md:text-sm text-blue-700">
          <span>
            🔎 {statusFilter === 'active' && !expiringDays && 'Faqat faol shartnomalar ko\'rsatilmoqda'}
            {expiringDays && `${expiringDays} kun ichida tugaydigan (faol) shartnomalar ko'rsatilmoqda`}
          </span>
          <Link to={`/accounting?tab=${contractTab}`} className="underline hover:no-underline whitespace-nowrap">Filtrni tozalash ✕</Link>
        </div>
      )}

      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
            <tr>
              <th className="px-2 md:px-4 py-3 text-left">Raqam / Sarlavha</th>
              <th className="px-2 md:px-4 py-3 text-left hidden md:table-cell">{isClientType ? 'Mijoz' : 'Yetkazib beruvchi'}</th>
              <th className="px-2 md:px-4 py-3 text-right">Summa</th>
              <th className="px-2 md:px-4 py-3 text-left">Holat</th>
              <th className="px-2 md:px-4 py-3 text-left hidden lg:table-cell">Mas'ul</th>
              <th className="px-2 md:px-4 py-3 text-left hidden xl:table-cell">Yaratuvchi</th>
              <th className="px-2 md:px-4 py-3 text-left hidden lg:table-cell">Boshlanish</th>
              <th className="px-2 md:px-4 py-3 text-left hidden lg:table-cell">Tugash</th>
              <th className="px-2 md:px-4 py-3 text-center hidden sm:table-cell">Fayl</th>
              <th className="px-2 md:px-4 py-3 text-center">Amallar</th>
            </tr>
          </thead>
          <tbody>
            {visibleContracts.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-10 text-gray-400">
                {cfg.icon} {cfg.label} shartnomalar yo'q
              </td></tr>
            ) : visibleContracts.map(c => (
              <tr key={c.id} className="border-t hover:bg-gray-50">
                <td className="px-2 md:px-4 py-3">
                  <div className="font-mono text-blue-600 font-medium text-xs">{c.number}</div>
                  <div className="font-medium max-w-xs truncate text-sm">{c.title}</div>
                  <div className="md:hidden text-xs text-gray-400 mt-0.5">{getPartyName(c)}</div>
                </td>
                <td className="px-2 md:px-4 py-3 text-gray-600 hidden md:table-cell">{getPartyName(c)}</td>
                <td className="px-2 md:px-4 py-3 text-right font-medium whitespace-nowrap">
                  {c.total_amount.toLocaleString()} {currencySymbol[c.currency]}
                </td>
                <td className="px-2 md:px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs whitespace-nowrap ${contractStatusConfig[c.status]?.color}`}>
                    {contractStatusConfig[c.status]?.label}
                  </span>
                </td>
                <td className="px-2 md:px-4 py-3 text-gray-600 text-xs hidden lg:table-cell">{c.assignee?.full_name || '—'}</td>
                <td className="px-2 md:px-4 py-3 text-gray-600 text-xs hidden xl:table-cell">{c.creator?.full_name || '—'}</td>
                <td className="px-2 md:px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">
                  {c.start_date ? new Date(c.start_date).toLocaleDateString('ru-RU') : '—'}
                </td>
                <td className="px-2 md:px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">
                  {c.end_date ? new Date(c.end_date).toLocaleDateString('ru-RU') : '—'}
                </td>
                <td className="px-2 md:px-4 py-3 text-center hidden sm:table-cell">
                  {c.file_url ? (
                    <span className="text-green-600 text-xs font-medium">📎 Bor</span>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </td>
                <td className="px-2 md:px-4 py-3">
                  <div className="flex justify-center gap-1">
                    <button onClick={() => setShowDetail(c)} title="Ko'rish"
                      className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs">👁️</button>
                    <button onClick={() => setShowExportModal(c.id)} title="Shartnomani yuklab olish"
                      className="px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 text-xs">📄</button>
                    <button onClick={() => {
                      if (!canFull) return;
                      setEditItem(c);
                      setContractForm({
                        contract_type: c.contract_type, title: c.title,
                        client_id: c.client?.id?.toString() || '',
                        supplier_id: c.supplier?.id?.toString() || '',
                        currency: c.currency, total_amount: c.total_amount,
                        transport_cost: c.transport_cost || 0,
                        status: c.status,
                        start_date: c.start_date ? c.start_date.split('T')[0] : '',
                        end_date: c.end_date ? c.end_date.split('T')[0] : '',
                        description: c.description || '',
                        assigned_to_id: c.assigned_to_id?.toString() || '',
                        delivery_method: c.delivery_method || DELIVERY_METHOD_DEFAULT,
                        iso_standard_option: c.iso_standard_option || ISO_STANDARD_DEFAULT,
                        iso_standard_other_text: c.iso_standard_other_text || '',
                        ...Object.fromEntries(CONTRACT_TERM_FIELDS.map(f => [f.key, c[f.key] ?? getTermDefault(f.key, c.contract_type)])),
                        final_payment_days: c.final_payment_days ?? SERVICE_TERM_DEFAULTS.final_payment_days,
                        execution_terms: c.execution_terms || '',
                        service_equipment: (c.service_equipment || []).map(r => ({ ...r, serial: r.serial || '' })),
                        service_items: (c.service_items || []).map(l => ({ ...l })),
                        external_contract_number: c.external_contract_number || '', contract_date: c.contract_date || '',
                      } as any);
                      setClientInstruments(null);
                      setGoodsAmount(c.total_amount - (c.transport_cost || 0));
                      setShowAdvancedTerms(false);
                      setError(''); setShowModal('contract');
                    }} disabled={!canFull}
                      title={!canFull ? "Faqat ko'rish huquqi" : "Tahrirlash"}
                      className={canFull ? "px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>✏️</button>
                    <button onClick={() => handleDelete('contracts', c.id)} disabled={!canFull}
                      title={!canFull ? "Faqat ko'rish huquqi" : "O'chirish"}
                      className={canFull ? "px-2 py-1 bg-red-100 text-red-700 rounded text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Contract Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          {/* xizmat shartnomasida hisoblar + to'lovlar paneli bor - summalar/muddatlar kesilmasin (G9) */}
          <div className={`bg-white rounded-2xl shadow-xl w-full ${showDetail.contract_type === 'service' ? 'max-w-4xl' : 'max-w-lg'} p-4 md:p-6 max-h-[90vh] overflow-y-auto`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-blue-600 font-bold text-lg">{showDetail.number}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${contractTypeConfig[showDetail.contract_type]?.color}`}>
                    {contractTypeConfig[showDetail.contract_type]?.icon} {contractTypeConfig[showDetail.contract_type]?.label}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-800">{showDetail.title}</h3>
                <p className="text-gray-500 text-sm">{getPartyName(showDetail)}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${contractStatusConfig[showDetail.status]?.color}`}>
                {contractStatusConfig[showDetail.status]?.label}
              </span>
            </div>

            {/* Ma'lumotlar */}
            <div className="space-y-2 text-sm mb-4">
              {showDetail.contract_type === 'supplier' && (showDetail.transport_cost || 0) > 0 ? (
                <>
                  <div className="flex justify-between border-b pb-2">
                    <span className="text-gray-500">Tovarlar summasi</span>
                    <span className="font-medium">
                      {(showDetail.total_amount - (showDetail.transport_cost || 0)).toLocaleString()} {currencySymbol[showDetail.currency]}
                    </span>
                  </div>
                  <div className="flex justify-between border-b pb-2">
                    <span className="text-gray-500">🚚 Transport xarajati</span>
                    <span className="font-medium">
                      {(showDetail.transport_cost || 0).toLocaleString()} {currencySymbol[showDetail.currency]}
                    </span>
                  </div>
                  <div className="flex justify-between border-b pb-2">
                    <span className="text-gray-500">Jami summa</span>
                    <span className="font-bold text-blue-600">
                      {showDetail.total_amount.toLocaleString()} {currencySymbol[showDetail.currency]}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Summa</span>
                  <span className="font-bold text-blue-600">
                    {showDetail.total_amount.toLocaleString()} {currencySymbol[showDetail.currency]}
                  </span>
                </div>
              )}
              {showDetail.contract_date && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Shartnoma sanasi</span>
                  <span>{showDetail.contract_date.split('-').reverse().join('.')}</span>
                </div>
              )}
              {showDetail.start_date && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Boshlanish</span>
                  <span>{new Date(showDetail.start_date).toLocaleDateString('ru-RU')}</span>
                </div>
              )}
              {showDetail.end_date && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Tugash</span>
                  <span>{new Date(showDetail.end_date).toLocaleDateString('ru-RU')}</span>
                </div>
              )}
              {showDetail.description && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                  <b>Tavsif:</b> {showDetail.description}
                </div>
              )}
              {(showDetail.assignee || showDetail.creator) && (
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-500">
                  {showDetail.assignee && (
                    <span>👤 <b className="text-gray-700">Mas'ul:</b> {showDetail.assignee.full_name}</span>
                  )}
                  {showDetail.creator && (
                    <span>✍️ <b className="text-gray-700">Yaratuvchi:</b> {showDetail.creator.full_name}</span>
                  )}
                </div>
              )}
            </div>

            {/* PDF Fayl yuklash */}
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">📎 Shartnoma fayli (PDF)</p>
              {showDetail.file_url ? (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
                  <span className="text-green-600 text-sm">✅ PDF fayl yuklangan</span>
                  <div className="flex gap-2 ml-auto">
                    <button onClick={() => handleFileView(showDetail.id)}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs hover:bg-blue-200">
                      👁️ Ko'rish
                    </button>
                    {canFull && (
                      <button onClick={() => handleFileDelete(showDetail.id)} title="Faylni o'chirish"
                        className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-xs hover:bg-red-200">
                        🗑️ O'chirish
                      </button>
                    )}
                  </div>
                </div>
              ) : canFull ? (
                <label className={`flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition ${uploadingFile ? 'opacity-50' : ''}`}>
                  <span className="text-lg mb-1">📄</span>
                  <span className="text-xs text-gray-500">
                    {uploadingFile ? 'Yuklanmoqda...' : 'Imzolangan shartnoma PDF sini yuklang'}
                  </span>
                  <input type="file" accept=".pdf" className="hidden" disabled={uploadingFile}
                    onChange={e => e.target.files && handleFileUpload(showDetail.id, e.target.files[0])} />
                </label>
              ) : (
                <div className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-gray-200 rounded-lg text-gray-400">
                  <span className="text-lg mb-1">📄</span>
                  <span className="text-xs">Fayl yuklanmagan</span>
                </div>
              )}
            </div>

            {/* Asosiy amallar */}
            <div className="flex justify-end gap-2 mb-2">
              <button onClick={() => { setShowExportModal(showDetail.id); }} title="Shartnomani yuklab olish"
                className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm">
                📄 Shablon
              </button>
              <button onClick={async () => {
                setShowVersions(v => !v);
                if (!showVersions) await loadVersions(showDetail.id);
              }}
                className="px-3 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm">
                🕐 Versiyalar
              </button>
              <button onClick={() => { if (canFull) { setShowDetail(null); setEditItem(showDetail); setContractForm({
                contract_type: showDetail.contract_type, title: showDetail.title,
                client_id: showDetail.client?.id?.toString() || '',
                supplier_id: showDetail.supplier?.id?.toString() || '',
                currency: showDetail.currency, total_amount: showDetail.total_amount,
                transport_cost: showDetail.transport_cost || 0,
                status: showDetail.status,
                start_date: showDetail.start_date ? showDetail.start_date.split('T')[0] : '',
                end_date: showDetail.end_date ? showDetail.end_date.split('T')[0] : '',
                description: showDetail.description || '',
                assigned_to_id: showDetail.assigned_to_id?.toString() || '',
                delivery_method: showDetail.delivery_method || DELIVERY_METHOD_DEFAULT,
                iso_standard_option: showDetail.iso_standard_option || ISO_STANDARD_DEFAULT,
                iso_standard_other_text: showDetail.iso_standard_other_text || '',
                ...Object.fromEntries(CONTRACT_TERM_FIELDS.map(f => [f.key, showDetail[f.key] ?? getTermDefault(f.key, showDetail.contract_type)])),
                final_payment_days: showDetail.final_payment_days ?? SERVICE_TERM_DEFAULTS.final_payment_days,
                execution_terms: showDetail.execution_terms || '',
                service_equipment: (showDetail.service_equipment || []).map(r => ({ ...r, serial: r.serial || '' })),
                service_items: (showDetail.service_items || []).map(l => ({ ...l })),
                external_contract_number: showDetail.external_contract_number || '', contract_date: showDetail.contract_date || '',
              } as any); setClientInstruments(null); setGoodsAmount(showDetail.total_amount - (showDetail.transport_cost || 0)); setShowAdvancedTerms(false); setShowModal('contract'); } }} disabled={!canFull}
                title={!canFull ? "Faqat ko'rish huquqi" : "Tahrirlash"}
                className={`px-3 py-1.5 rounded-lg text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>
                ✏️ Tahrirlash
              </button>
              <button onClick={() => { setShowDetail(null); setShowVersions(false); setVersionComment(''); setContractVersions([]); }} title="Yopish"
                className="px-3 py-1.5 border rounded-lg hover:bg-gray-50 text-sm">Yopish</button>
            </div>

            {/* Zanjir tugmalari - xizmat shartnomasida eski invoys yo'li yo'q (H6): hisoblar
                pastdagi "To'lov uchun hisoblar" bo'limida (server ham eski yo'lni rad etadi) */}
            {showDetail.contract_type !== 'service' && (
            <div className="flex justify-end gap-2 pt-2 border-t">
              <span className="text-xs text-gray-400 self-center mr-1">Keyingi qadam:</span>
              <button onClick={() => handleCreateInvoice(showDetail.id, showDetail.number)}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                🧾 Invoys yaratish
              </button>
              <button onClick={() => {
                const tab = showDetail.contract_type === 'supplier' ? 'inv_purchase' : 'inv_supply';
                setActiveTab(tab as any);
              }}
                className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm">
                📋 Invoyslarni ko'rish
              </button>
            </div>
            )}

            {showDetail.contract_type === 'service' && canView('service_acts') && <ServiceActsPanel contractId={showDetail.id} canFull={canEdit('service_acts')} />}
            {/* (E) avans/yakuniy hisob-fakturalar - faqat buxgalteriya / rahbariyat (server ham tekshiradi) */}
            {showDetail.contract_type === 'service' && (user?.access_level === 'company' || user?.department === 'accounting') && (
              <ServiceInvoicesPanel contractId={showDetail.id} canFull={canEdit('accounting')} clientId={showDetail.client?.id} currency={showDetail.currency} />
            )}

            {/* Versiyalar tarixi paneli */}
            {showVersions && (
              <div className="mt-4 border-t pt-4">
                <h4 className="font-semibold text-gray-700 text-sm mb-3">🕐 Versiyalar tarixi</h4>

                {/* Yangi izoh qo'shish */}
                <div className="flex gap-2 mb-3">
                  <input
                    value={versionComment}
                    onChange={e => setVersionComment(e.target.value)}
                    placeholder="O'zgarish izohi (masalan: 2 dona qilindi)"
                    className="flex-1 border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                  <button onClick={() => handleAddVersion(showDetail.id)} disabled={addingVersion || !versionComment.trim()}
                    className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 disabled:opacity-50">
                    {addingVersion ? '...' : '+ Izoh'}
                  </button>
                </div>

                {/* Versiyalar ro'yxati */}
                {contractVersions.length === 0 ? (
                  <p className="text-gray-400 text-xs text-center py-3">Versiyalar yo'q</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {contractVersions.map(v => (
                      <div key={v.id} className="bg-gray-50 rounded-lg p-3 text-sm">
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex items-center gap-2">
                            <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-bold">
                              V{v.version}
                            </span>
                            <span className="text-gray-500 text-xs">
                              {new Date(v.created_at).toLocaleDateString('ru-RU')} —{' '}
                              {v.changer?.full_name || '—'}
                            </span>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => handleVersionExport(showDetail.id, v.id, v.version, 'pdf', showDetail.contract_type)}
                              className="px-2 py-0.5 bg-red-100 text-red-600 rounded text-xs hover:bg-red-200">PDF</button>
                            <button onClick={() => handleVersionExport(showDetail.id, v.id, v.version, 'docx', showDetail.contract_type)}
                              className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded text-xs hover:bg-blue-200">DOCX</button>
                          </div>
                        </div>
                        {v.change_comment && (
                          <p className="text-gray-600 text-xs italic">"{v.change_comment}"</p>
                        )}
                        {v.total_amount != null && (
                          <p className="text-gray-500 text-xs mt-1">
                            Summa: {v.total_amount.toLocaleString()} {currencySymbol[v.currency || ''] || v.currency}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

  const tabs: { key: TabType; label: string; icon: string; group?: string }[] = [
    { key: 'summary',      label: 'Xulosa',          icon: '📊' },
    { key: 'supply',       label: 'Yetkazib berish',  icon: '📦', group: 'Shartnomalar' },
    { key: 'purchase',     label: 'Xarid',            icon: '🛒', group: 'Shartnomalar' },
    { key: 'service',      label: 'Xizmat',           icon: '🔧', group: 'Shartnomalar' },
    { key: 'inv_supply',   label: 'Yetkazib berish',  icon: '📦', group: 'Invoicelar' },
    { key: 'inv_purchase', label: 'Xarid',            icon: '🛒', group: 'Invoicelar' },
    { key: 'inv_service',  label: 'Xizmat (eski)',    icon: '🔧', group: 'Invoicelar' },
    { key: 'transactions', label: "To'lovlar",        icon: '💳' },
    { key: 'expenses',     label: 'Xarajatlar',       icon: '💸' },
    { key: 'calendar',     label: 'Ish kalendari',    icon: '📅' },
  ];

  // sales_head -> faqat "Yetkazib berish" (client) shartnomasi, service_head
  // -> faqat "Xizmat" (service) shartnomasi. Boshqa hamma tab (Xarid
  // shartnomasi, barcha Invoice/To'lov/Xarajat/Xulosa) yashiriladi - bular
  // buxgalteriya bo'limining o'zi, sotuv/servis boshlig'iga tegishli emas.
  const visibleTabs = restrictedDept
    ? tabs.filter(t => t.key === (restrictedDept === 'sales' ? 'supply' : 'service'))
    : tabs;
  const contractTabs = visibleTabs.filter(t => t.group === 'Shartnomalar');
  const invoiceTabs = visibleTabs.filter(t => t.group === 'Invoicelar');
  const otherTabs = visibleTabs.filter(t => !t.group);

  return (
    <div className="p-3 md:p-6">
      <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-6">Buxgalteriya</h2>

      {/* Tabs */}
      <div className="mb-4 md:mb-6">
        {/* Mobile: barchasi bitta gorizontal scroll qatorda */}
        <div className="flex md:flex-wrap gap-2 overflow-x-auto pb-2 md:pb-0 -mx-3 px-3 md:mx-0 md:px-0">
          {otherTabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.key ? 'bg-blue-600 text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-100 shadow'
              }`}>
              {tab.icon} {tab.label}
            </button>
          ))}

          {/* Shartnomalar guruhi */}
          <div className="flex items-center gap-1 bg-white rounded-xl shadow px-2 py-1 flex-shrink-0">
            <span className="text-xs text-gray-500 font-medium mr-1 whitespace-nowrap">📝 Shartnomalar:</span>
            {contractTabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`px-2 md:px-3 py-1.5 rounded-lg text-xs font-medium transition whitespace-nowrap ${
                  activeTab === tab.key ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Invoicelar guruhi */}
          <div className="flex items-center gap-1 bg-white rounded-xl shadow px-2 py-1 flex-shrink-0">
            <span className="text-xs text-gray-500 font-medium mr-1 whitespace-nowrap">🧾 Invoyslar:</span>
            {invoiceTabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`px-2 md:px-3 py-1.5 rounded-lg text-xs font-medium transition whitespace-nowrap ${
                  activeTab === tab.key ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ===== XULOSA ===== */}
      {activeTab === 'summary' && summary && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
            <div className="bg-white rounded-xl shadow p-3 md:p-5">
              <p className="text-gray-500 text-xs md:text-sm mb-1 leading-tight">Kirim (USD)</p>
              <p className="text-lg md:text-2xl font-bold text-green-600">${summary.income_usd.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-3 md:p-5">
              <p className="text-gray-500 text-xs md:text-sm mb-1 leading-tight">Kirim (UZS)</p>
              <p className="text-lg md:text-2xl font-bold text-green-600">{summary.income_uzs.toLocaleString()} so'm</p>
            </div>
            <div className="bg-white rounded-xl shadow p-3 md:p-5">
              <p className="text-gray-500 text-xs md:text-sm mb-1 leading-tight">Xarajat (USD)</p>
              <p className="text-lg md:text-2xl font-bold text-red-500">${summary.expense_usd.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-3 md:p-5">
              <p className="text-gray-500 text-xs md:text-sm mb-1 leading-tight">Xarajat (UZS)</p>
              <p className="text-lg md:text-2xl font-bold text-red-500">{summary.expense_uzs.toLocaleString()} so'm</p>
            </div>
            <div className="bg-white rounded-xl shadow p-3 md:p-5 border-l-4 border-blue-500">
              <p className="text-gray-500 text-xs md:text-sm mb-1 leading-tight">Foyda (USD)</p>
              <p className={`text-lg md:text-2xl font-bold ${summary.profit_usd >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                ${summary.profit_usd.toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow p-3 md:p-5 border-l-4 border-blue-500">
              <p className="text-gray-500 text-xs md:text-sm mb-1 leading-tight">Foyda (UZS)</p>
              <p className={`text-lg md:text-2xl font-bold ${summary.profit_uzs >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                {summary.profit_uzs.toLocaleString()} so'm
              </p>
            </div>
            <div className="bg-white rounded-xl shadow p-3 md:p-5">
              <p className="text-gray-500 text-xs md:text-sm mb-1 leading-tight">To'lanmagan invoicelar</p>
              <p className="text-lg md:text-2xl font-bold text-orange-500">{summary.unpaid_invoices}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-3 md:p-5">
              <p className="text-gray-500 text-xs md:text-sm mb-1 leading-tight">Faol shartnomalar</p>
              <p className="text-lg md:text-2xl font-bold text-green-600">{summary.active_contracts}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-4">
            {(['supply', 'purchase', 'service'] as ContractTabType[]).map(ct => {
              const cfg = contractTypeConfig[contractTabMap[ct]];
              const count = getContractsByType(ct).length;
              const active = getContractsByType(ct).filter(c => c.status === 'active').length;
              return (
                <div key={ct} className="bg-white rounded-xl shadow p-3 md:p-4 cursor-pointer hover:shadow-md transition"
                  onClick={() => setActiveTab(ct)}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className="text-gray-500 text-xs md:text-sm leading-tight">{cfg.icon} {cfg.label} shartnomalar</p>
                      <p className="text-xl md:text-2xl font-bold text-gray-800 mt-1">{count}</p>
                      <p className="text-xs text-green-600 mt-1">Faol: {active}</p>
                    </div>
                    <span className={`hidden sm:inline-block px-2 py-1 rounded-full text-xs whitespace-nowrap flex-shrink-0 ${cfg.color}`}>{cfg.label}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <div className="bg-white rounded-xl shadow p-4">
              <h3 className="font-semibold text-gray-700 mb-3">Oxirgi to'lovlar</h3>
              {transactions.slice(0, 5).map(t => (
                <div key={t.id} className="flex justify-between items-center py-2 border-b last:border-0 text-sm">
                  <div>
                    <span className={`font-medium ${t.transaction_type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                      {t.transaction_type === 'income' ? '↑' : '↓'}
                    </span>
                    <span className="ml-2 text-gray-600">{t.description || '—'}</span>
                  </div>
                  <span className="font-bold">{t.amount.toLocaleString()} {currencySymbol[t.currency]}</span>
                </div>
              ))}
              {transactions.length === 0 && <p className="text-gray-400 text-sm">To'lovlar yo'q</p>}
            </div>
            <div className="bg-white rounded-xl shadow p-4">
              <h3 className="font-semibold text-gray-700 mb-3">Oxirgi xarajatlar</h3>
              {expenses.slice(0, 5).map(e => (
                <div key={e.id} className="flex justify-between items-center py-2 border-b last:border-0 text-sm">
                  <div className="flex items-center gap-2">
                    <span>{expenseCategoryConfig[e.category]?.icon}</span>
                    <span className="text-gray-600">{e.title}</span>
                  </div>
                  <span className="font-bold text-red-500">{e.amount.toLocaleString()} {currencySymbol[e.currency]}</span>
                </div>
              ))}
              {expenses.length === 0 && <p className="text-gray-400 text-sm">Xarajatlar yo'q</p>}
            </div>
          </div>
        </div>
      )}

      {/* ===== SHARTNOMALAR ===== */}
      {activeTab === 'supply' && <ContractTable contractTab="supply" />}
      {activeTab === 'purchase' && <ContractTable contractTab="purchase" />}
      {activeTab === 'service' && <ContractTable contractTab="service" />}

      {/* ===== INVOICELAR ===== */}
      {activeTab === 'inv_supply' && <InvoiceTable invoiceTab="inv_supply" />}
      {activeTab === 'inv_purchase' && <InvoiceTable invoiceTab="inv_purchase" />}
      {activeTab === 'inv_service' && <InvoiceTable invoiceTab="inv_service" />}

      {/* ===== TO'LOVLAR ===== */}
      {activeTab === 'calendar' && <HolidayCalendar canFull={canFull} />}

      {activeTab === 'transactions' && (
        <div>
          <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
            <div className="flex gap-2 md:gap-3">
              <div className="bg-white rounded-xl shadow px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm">
                Kirim: <b className="text-green-600">{transactions.filter(t => t.transaction_type === 'income').length}</b>
              </div>
              <div className="bg-white rounded-xl shadow px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm">
                Chiqim: <b className="text-red-500">{transactions.filter(t => t.transaction_type === 'expense').length}</b>
              </div>
            </div>
            <button onClick={() => {
              if (!canFull) return;
              setEditItem(null);
              setTransactionForm({ transaction_type: 'income', invoice_id: '', client_id: '', supplier_id: '', currency: 'usd', amount: 0, description: '', transaction_date: '' });
              setError('');
              setShowModal('transaction');
            }} disabled={!canFull}
              title={!canFull ? "Faqat ko'rish huquqi" : undefined}
              className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>
              + Yangi to'lov
            </button>
          </div>

          <div className="bg-white rounded-xl shadow overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-2 md:px-4 py-3 text-left">Tur / Tomon</th>
                  <th className="px-2 md:px-4 py-3 text-left hidden md:table-cell">Tavsif</th>
                  <th className="px-2 md:px-4 py-3 text-right">Summa</th>
                  <th className="px-2 md:px-4 py-3 text-left hidden sm:table-cell">Sana</th>
                  <th className="px-2 md:px-4 py-3 text-center">Amallar</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-10 text-gray-400">To'lovlar yo'q</td></tr>
                ) : transactions.map(t => (
                  <tr key={t.id} className="border-t hover:bg-gray-50">
                    <td className="px-2 md:px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${t.transaction_type === 'income' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {t.transaction_type === 'income' ? '↑ Kirim' : '↓ Chiqim'}
                      </span>
                      <div className="text-gray-600 text-sm mt-0.5">{getPartyName(t)}</div>
                      <div className="md:hidden text-xs text-gray-400">{t.description || '—'}</div>
                      <div className="sm:hidden text-xs text-gray-400">{new Date(t.transaction_date).toLocaleDateString('ru-RU')}</div>
                    </td>
                    <td className="px-2 md:px-4 py-3 text-gray-600 hidden md:table-cell">{t.description || '—'}</td>
                    <td className={`px-2 md:px-4 py-3 text-right font-bold whitespace-nowrap ${t.transaction_type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                      {t.amount.toLocaleString()} {currencySymbol[t.currency]}
                    </td>
                    <td className="px-2 md:px-4 py-3 text-gray-500 text-xs hidden sm:table-cell">
                      {new Date(t.transaction_date).toLocaleDateString('ru-RU')}
                    </td>
                    <td className="px-2 md:px-4 py-3">
                      <div className="flex justify-center gap-1">
                        <button onClick={() => {
                          if (!canFull) return;
                          setEditItem(t);
                          setTransactionForm({
                            transaction_type: t.transaction_type,
                            invoice_id: t.invoice_id?.toString() || '',
                            client_id: t.client?.id?.toString() || '',
                            supplier_id: t.supplier?.id?.toString() || '',
                            currency: t.currency,
                            amount: t.amount,
                            description: t.description || '',
                            transaction_date: t.transaction_date ? t.transaction_date.split('T')[0] : '',
                          });
                          setError('');
                          setShowModal('transaction');
                        }} disabled={!canFull}
                          title={!canFull ? "Faqat ko'rish huquqi" : "Tahrirlash"}
                          className={canFull ? "px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>✏️</button>
                        <button onClick={() => handleDelete('transactions', t.id)} disabled={!canFull}
                          title={!canFull ? "Faqat ko'rish huquqi" : "O'chirish"}
                          className={canFull ? "px-2 py-1 bg-red-100 text-red-700 rounded text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== XARAJATLAR ===== */}
      {activeTab === 'expenses' && (
        <div>
          <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
            <div className="flex gap-2 flex-wrap">
              {Object.entries(expenseCategoryConfig).map(([k, v]) => (
                <div key={k} className="bg-white rounded-xl shadow px-2 md:px-3 py-2 text-xs">
                  {v.icon} {v.label}: <b>{expenses.filter(e => e.category === k).length}</b>
                </div>
              ))}
            </div>
            <button onClick={() => {
              if (!canFull) return;
              setEditItem(null);
              setExpenseForm({ title: '', category: 'other', currency: 'uzs', amount: 0, description: '', expense_date: '' });
              setShowModal('expense');
            }} disabled={!canFull}
              title={!canFull ? "Faqat ko'rish huquqi" : undefined}
              className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>
              + Yangi xarajat
            </button>
          </div>

          <div className="bg-white rounded-xl shadow overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-2 md:px-4 py-3 text-left">Sarlavha / Kategoriya</th>
                  <th className="px-2 md:px-4 py-3 text-right">Summa</th>
                  <th className="px-2 md:px-4 py-3 text-left hidden md:table-cell">Tavsif</th>
                  <th className="px-2 md:px-4 py-3 text-left hidden sm:table-cell">Sana</th>
                  <th className="px-2 md:px-4 py-3 text-center">Amallar</th>
                </tr>
              </thead>
              <tbody>
                {expenses.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-10 text-gray-400">Xarajatlar yo'q</td></tr>
                ) : expenses.map(e => (
                  <tr key={e.id} className="border-t hover:bg-gray-50">
                    <td className="px-2 md:px-4 py-3">
                      <div className="font-medium">{e.title}</div>
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs inline-block mt-1">
                        {expenseCategoryConfig[e.category]?.icon} {expenseCategoryConfig[e.category]?.label}
                      </span>
                      <div className="sm:hidden text-xs text-gray-400 mt-0.5">{new Date(e.expense_date).toLocaleDateString('ru-RU')}</div>
                    </td>
                    <td className="px-2 md:px-4 py-3 text-right font-bold text-red-500 whitespace-nowrap">
                      {e.amount.toLocaleString()} {currencySymbol[e.currency]}
                    </td>
                    <td className="px-2 md:px-4 py-3 text-gray-500 hidden md:table-cell">{e.description || '—'}</td>
                    <td className="px-2 md:px-4 py-3 text-gray-500 text-xs hidden sm:table-cell">
                      {new Date(e.expense_date).toLocaleDateString('ru-RU')}
                    </td>
                    <td className="px-2 md:px-4 py-3">
                      <div className="flex justify-center gap-1">
                        <button onClick={() => {
                          if (!canFull) return;
                          setEditItem(e);
                          setExpenseForm({
                            title: e.title, category: e.category,
                            currency: e.currency, amount: e.amount,
                            description: e.description || '',
                            expense_date: e.expense_date.split('T')[0],
                          });
                          setShowModal('expense');
                        }} disabled={!canFull}
                          title={!canFull ? "Faqat ko'rish huquqi" : "Tahrirlash"}
                          className={canFull ? "px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>✏️</button>
                        <button onClick={() => handleDelete('expenses', e.id)} disabled={!canFull}
                          title={!canFull ? "Faqat ko'rish huquqi" : "O'chirish"}
                          className={canFull ? "px-2 py-1 bg-red-100 text-red-700 rounded text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== SHARTNOMA MODAL ===== */}
      {showModal === 'contract' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">
              {editItem ? 'Shartnomani tahrirlash' : 'Yangi shartnoma'}
            </h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            <div className="flex gap-2 mb-4">
              {[
                { val: 'client',   label: '📦 Yetkazib berish' },
                { val: 'supplier', label: '🛒 Xarid' },
                { val: 'service',  label: '🔧 Xizmat' },
              ].map(t => (
                <button key={t.val} onClick={() => setContractForm({
                    ...contractForm, contract_type: t.val,
                    // Yangi shartnoma yaratishda tur almashtirilsa,
                    // delivery_days/prepayment_days ham shu turga mos
                    // standart qiymatga qaytariladi (tahrirlashda tegilmaydi).
                    ...(!editItem ? (t.val === 'supplier' ? PURCHASE_TERM_DEFAULTS
                      : t.val === 'service' ? SERVICE_TERM_DEFAULTS
                      : { delivery_days: CONTRACT_TERM_DEFAULTS.delivery_days, prepayment_days: CONTRACT_TERM_DEFAULTS.prepayment_days, prepayment_percent: CONTRACT_TERM_DEFAULTS.prepayment_percent }) : {}),
                  })}
                  className={`flex-1 py-2 rounded-lg border-2 text-xs font-medium transition ${
                    contractForm.contract_type === t.val ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Sarlavha *</label>
                <input value={contractForm.title} onChange={e => setContractForm({ ...contractForm, title: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {contractForm.contract_type === 'supplier' ? (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yetkazib beruvchi</label>
                  <select value={contractForm.supplier_id} onChange={e => setContractForm({ ...contractForm, supplier_id: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">— Tanlang —</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.company_name}</option>)}
                  </select>
                </div>
              ) : (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mijoz</label>
                  <select value={contractForm.client_id} onChange={e => setContractForm({ ...contractForm, client_id: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">— Tanlang —</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_person}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valyuta</label>
                <select value={contractForm.currency} onChange={e => setContractForm({ ...contractForm, currency: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="usd">USD</option><option value="eur">EUR</option><option value="uzs">UZS</option>
                </select>
              </div>
              {contractForm.contract_type === 'supplier' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tovarlar summasi</label>
                    <input type="number" value={goodsAmount}
                      onChange={e => setGoodsAmount(Number(e.target.value))}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">🚚 Transport xarajati</label>
                    <input type="number" value={contractForm.transport_cost}
                      onChange={e => setContractForm({ ...contractForm, transport_cost: Number(e.target.value) })}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex justify-between items-center">
                    <span className="text-sm text-blue-700 font-medium">Jami summa:</span>
                    <span className="text-lg font-bold text-blue-700">
                      {(Number(goodsAmount) + Number(contractForm.transport_cost || 0)).toLocaleString()} {contractForm.currency.toUpperCase()}
                    </span>
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Summa</label>
                  <input type="number" value={contractForm.total_amount} onChange={e => setContractForm({ ...contractForm, total_amount: Number(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Boshlanish</label>
                <input type="date" value={contractForm.start_date} onChange={e => setContractForm({ ...contractForm, start_date: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tugash</label>
                <input type="date" value={contractForm.end_date} onChange={e => setContractForm({ ...contractForm, end_date: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Holat</label>
                <select value={contractForm.status} onChange={e => setContractForm({ ...contractForm, status: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {Object.entries(contractStatusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mas'ul hodim</label>
                <select value={contractForm.assigned_to_id}
                  onChange={e => setContractForm({ ...contractForm, assigned_to_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Mas'ul hodimni tanlang —</option>
                  {Array.from(new Set(assignableUsers.map(u => u.role)))
                    .sort((a, b) => {
                      const ia = ROLE_GROUP_ORDER.indexOf(a);
                      const ib = ROLE_GROUP_ORDER.indexOf(b);
                      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
                    })
                    .map(role => {
                      const groupUsers = assignableUsers.filter(u => u.role === role);
                      const groupLabel = groupUsers[0]?.role_label || role;
                      return (
                        <optgroup key={role} label={groupLabel}>
                          {groupUsers.map(u => (
                            <option key={u.id} value={u.id}>{u.full_name}</option>
                          ))}
                        </optgroup>
                      );
                    })}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tavsif</label>
                <textarea value={contractForm.description} onChange={e => setContractForm({ ...contractForm, description: e.target.value })}
                  rows={2} className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>

            {contractForm.contract_type === 'service' && (
              <div className="mt-4 border rounded-lg p-3">
                <div className="flex justify-between items-center mb-2 gap-2 flex-wrap">
                  <p className="text-sm font-medium text-gray-700">🔬 Ilova №1 — uskunalar ro'yxati</p>
                  <div className="flex gap-2">
                    {contractForm.client_id && (
                      <button type="button" onClick={async () => {
                        try {
                          const res = await api.get('/instruments', { params: { client_id: Number(contractForm.client_id) } });
                          setClientInstruments(res.data);
                        } catch { setClientInstruments(null); setError("Mijoz uskunalarini yuklab bo'lmadi (ruxsat yo'q bo'lishi mumkin) - qo'lda kiriting"); }
                      }} className="px-2 py-1 text-xs border rounded-lg hover:bg-gray-50">Mijoz uskunalaridan</button>
                    )}
                    <button type="button" onClick={() => setContractForm({
                      ...contractForm,
                      service_equipment: [...contractForm.service_equipment, { name: '', serial: '', category: null }],
                    })} className="px-2 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Qator</button>
                  </div>
                </div>
                {clientInstruments && (
                  <div className="mb-2 p-2 bg-gray-50 rounded-lg text-xs">
                    {clientInstruments.length === 0 ? <p className="text-gray-400">Bu mijozga bog'langan uskuna yo'q</p> : (
                      <div className="flex flex-wrap gap-1">
                        {clientInstruments.map(inst => (
                          <button type="button" key={inst.id} onClick={() => setContractForm({
                            ...contractForm,
                            service_equipment: [...contractForm.service_equipment, {
                              name: [inst.manufacturer, inst.model].filter(Boolean).join(' '),
                              serial: inst.serial_number || '', category: null,
                            }],
                          })} className="px-2 py-1 border rounded bg-white hover:bg-blue-50">
                            + {[inst.manufacturer, inst.model].filter(Boolean).join(' ')}{inst.serial_number ? ` (${inst.serial_number})` : ''}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {contractForm.service_equipment.length === 0 && (
                  <p className="text-xs text-gray-400">Qator qo'shilmasa - Ilova №1 qo'lda to'ldirish uchun bo'sh jadval bilan chiqadi.</p>
                )}
                <p className="text-xs text-gray-400 mb-2">
                  Toifa - faqat ta'mirlanadigan uskuna uchun (4.3-band kafolati toifaga qarab). Na bu yerda, na Ilova №2 qatorlarida toifa
                  bo'lmasa - shartnomada ta'mirlash ishi yo'q deb hisoblanadi va Ilova №1 da &quot;Toifa&quot; / &quot;Kafolat (4.3-band)&quot; ustunlari chiqmaydi.
                </p>
                <div className="space-y-2">
                  {contractForm.service_equipment.map((row, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <input value={row.name} placeholder="Nomi *"
                        onChange={e => { const rows = [...contractForm.service_equipment]; rows[idx] = { ...row, name: e.target.value }; setContractForm({ ...contractForm, service_equipment: rows }); }}
                        className="col-span-12 md:col-span-5 border rounded-lg px-2 py-1.5 text-sm" />
                      <input value={row.serial || ''} placeholder="Model / seriya № (ixtiyoriy)"
                        onChange={e => { const rows = [...contractForm.service_equipment]; rows[idx] = { ...row, serial: e.target.value }; setContractForm({ ...contractForm, service_equipment: rows }); }}
                        className="col-span-6 md:col-span-3 border rounded-lg px-2 py-1.5 text-sm" />
                      <select value={row.category || ''}
                        onChange={e => { const rows = [...contractForm.service_equipment]; rows[idx] = { ...row, category: e.target.value || null }; setContractForm({ ...contractForm, service_equipment: rows }); }}
                        className="col-span-5 md:col-span-3 border rounded-lg px-2 py-1.5 text-sm">
                        <option value="">— toifa tanlanmagan —</option>
                        {EQUIPMENT_CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <button type="button" title="O'chirish"
                        onClick={() => setContractForm({ ...contractForm, service_equipment: contractForm.service_equipment.filter((_, i) => i !== idx) })}
                        className="col-span-1 text-red-500 hover:text-red-700">✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {contractForm.contract_type === 'service' && (
              <div className="mt-4 border rounded-lg p-3 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shartnoma sanasi (hujjatdagi «№ … от»)</label>
                  <input type="date" value={contractForm.contract_date}
                    onChange={e => setContractForm({ ...contractForm, contract_date: e.target.value })}
                    className="w-full md:w-1/2 border rounded-lg px-3 py-2 text-sm" />
                  <p className="text-[11px] text-gray-400 mt-1">Bo&apos;sh - bugungi sana. Avans muddati shu sanadan hisoblanadi; faol shartnomada faqat buxgalteriya/rahbariyat o&apos;zgartiradi (tarixda qoladi).</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ro&apos;yxatga olish raqami (ixtiyoriy, har shartnomaning o&apos;zi)</label>
                  <input value={contractForm.external_contract_number}
                    onChange={e => setContractForm({ ...contractForm, external_contract_number: e.target.value })}
                    placeholder="masalan: 4145826"
                    className="w-full md:w-1/2 border rounded-lg px-3 py-2 text-sm" />
                  <p className="text-[11px] text-gray-400 mt-1">To&apos;ldirilsa shartnoma, dalolatnoma va to&apos;lov uchun hisobda ko&apos;rsatiladi. Boshqa shartnomada bor raqam qabul qilinmaydi.</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">🧾 Ilova №2 — xizmatlar (taklifdan avtomatik, tahrirlanadi)</p>
                  <p className="text-[11px] text-gray-400 mb-2">
                    Qatorlar bo&apos;lsa shartnoma summasi ularning yig&apos;indisiga teng qilinadi. Bo&apos;sh bo&apos;lsa Ilova №2 bog&apos;langan taklifdan olinadi.
                    Kafolat ustuni barcha qatorlarda bo&apos;sh bo&apos;lsa jadvalda ko&apos;rinmaydi.
                  </p>
                  <ServiceLinesEditor
                    lines={contractForm.service_items}
                    onChange={lines => setContractForm({ ...contractForm, service_items: lines })}
                    catalog={[]}
                    instruments={null}
                    instrumentsHint="Uskunani ro'yxatdan tanlash - Case/taklifda; bu yerda taklifdan kelgan uskuna saqlanadi yoki erkin matn kiritiladi."
                  />
                </div>
              </div>
            )}

            <div className="mt-4 border rounded-lg overflow-hidden">
              <button type="button" onClick={() => setShowAdvancedTerms(v => !v)}
                className="w-full flex justify-between items-center px-3 py-2 bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-700">
                <span>⚙️ Qo'shimcha shartnoma shartlari</span>
                <span className={`text-xs transition-transform ${showAdvancedTerms ? 'rotate-180' : ''}`}>▾</span>
              </button>
              {showAdvancedTerms && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-white">
                  {contractForm.contract_type !== 'service' && (<div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Yetkazib berish usuli</label>
                    <select value={contractForm.delivery_method}
                      onChange={e => setContractForm({ ...contractForm, delivery_method: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {DELIVERY_METHOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>)}
                  {contractForm.contract_type === 'supplier' && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sifat standarti (band 4.1)</label>
                      <select value={contractForm.iso_standard_option}
                        onChange={e => setContractForm({ ...contractForm, iso_standard_option: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                        {ISO_STANDARD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      {contractForm.iso_standard_option === 'other' && (
                        <input value={contractForm.iso_standard_other_text}
                          onChange={e => setContractForm({ ...contractForm, iso_standard_other_text: e.target.value })}
                          placeholder="Masalan: GOST 12345-67"
                          className="w-full border rounded-lg px-3 py-2 mt-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      )}
                    </div>
                  )}
                  {CONTRACT_TERM_FIELDS
                    .filter(f => contractForm.contract_type !== 'service' || SERVICE_TERM_KEYS.includes(f.key))
                    .map(f => (
                    <div key={f.key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {contractForm.contract_type === 'supplier' && PURCHASE_TERM_LABELS[f.key] ? PURCHASE_TERM_LABELS[f.key] : f.label}
                      </label>
                      <input type="number" step={f.step} value={contractForm[f.key]}
                        onChange={e => setContractForm({ ...contractForm, [f.key]: Number(e.target.value) })}
                        className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  ))}
                  {contractForm.contract_type === 'service' && (
                    <p className="text-xs text-gray-500 md:col-span-2">
                      QQS stavkasi - kompaniya sozlamalaridan (qoralamada joriy qiymat, shartnoma faollashganda muzlatiladi).
                    </p>
                  )}
                  {contractForm.contract_type === 'service' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Yakuniy to'lov muddati (ish kuni, dalolatnomadan keyin)</label>
                      <input type="number" step={1} value={contractForm.final_payment_days}
                        onChange={e => setContractForm({ ...contractForm, final_payment_days: Number(e.target.value) })}
                        className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  )}
                  {contractForm.contract_type === 'service' && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ishlarni bajarish muddati va tartibi (3.1-band)</label>
                      <textarea value={contractForm.execution_terms} rows={2}
                        onChange={e => setContractForm({ ...contractForm, execution_terms: e.target.value })}
                        placeholder="Masalan: ariza olingandan keyin 5 ish kuni ichida, Buyurtmachi hududida"
                        className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <p className="text-xs text-gray-400 mt-1">
                        Taklifdagi &quot;bajarish muddati&quot;dan o&apos;tadi. Matn PDF&apos;ga o&apos;zgarishsiz chiqadi - shartnoma qaysi tilda
                        yuklanadigan bo&apos;lsa, shu tilda yozing. Bo&apos;sh bo&apos;lsa - umumiy jumla (&quot;xizmatlar ko&apos;rsatish tugallangan paytgacha&quot;).
                      </p>
                    </div>
                  )}
                  {contractForm.contract_type === 'service' && (
                    <p className="col-span-2 text-xs text-gray-400">
                      Narxlar QQS ichida. Kompaniya QQS to'lovchi bo'lmasa (Kompaniya ma'lumotlari) - QQS foizi e'tiborga olinmaydi, shartnomada "без НДС".
                      Amal qilish muddati (10.1-band) - "Tugash sanasi" maydonidan.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setShowModal(null); setError(''); }} className="px-4 py-2 border rounded-lg">Bekor</button>
              <button onClick={handleContractSubmit} disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== INVOICE MODAL ===== */}
      {showModal === 'invoice' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">{editItem ? 'Invoice tahrirlash' : 'Yangi invoice'}</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            <div className="flex gap-2 mb-4">
              {[
                { val: 'client',   label: '📦 Yetkazib berish' },
                { val: 'supplier', label: '🛒 Xarid' },
                { val: 'service',  label: '🔧 Xizmat' },
              ].map(t => (
                <button key={t.val} onClick={() => setInvoiceForm({ ...invoiceForm, invoice_type: t.val })}
                  className={`flex-1 py-2 rounded-lg border-2 text-xs font-medium transition ${
                    invoiceForm.invoice_type === t.val ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {invoiceForm.invoice_type === 'supplier' ? (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yetkazib beruvchi</label>
                  <select value={invoiceForm.supplier_id} onChange={e => setInvoiceForm({ ...invoiceForm, supplier_id: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">— Tanlang —</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.company_name}</option>)}
                  </select>
                </div>
              ) : (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mijoz</label>
                  <select value={invoiceForm.client_id} onChange={e => setInvoiceForm({ ...invoiceForm, client_id: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">— Tanlang —</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_person}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shartnoma</label>
                <select value={invoiceForm.contract_id} onChange={e => {
                  const contractId = e.target.value;
                  const selected = contracts.find(c => c.id === Number(contractId));
                  if (selected) {
                    setInvoiceForm({
                      ...invoiceForm,
                      contract_id: contractId,
                      amount: selected.total_amount,
                      currency: selected.currency,
                    });
                  } else {
                    setInvoiceForm({ ...invoiceForm, contract_id: contractId });
                  }
                }}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {contracts.map(c => <option key={c.id} value={c.id}>{c.number} — {c.title} ({c.total_amount.toLocaleString()} {c.currency.toUpperCase()})</option>)}
                </select>
                {invoiceForm.contract_id && (
                  <p className="text-xs text-blue-500 mt-1">💡 Summa shartnomadan avtomatik olindi, kerak bo'lsa o'zgartirishingiz mumkin</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valyuta</label>
                <select value={invoiceForm.currency} onChange={e => setInvoiceForm({ ...invoiceForm, currency: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="usd">USD</option><option value="eur">EUR</option><option value="uzs">UZS</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Summa</label>
                <input type="number" value={invoiceForm.amount} onChange={e => setInvoiceForm({ ...invoiceForm, amount: Number(e.target.value) })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To'lov muddati</label>
                <input type="date" value={invoiceForm.due_date} onChange={e => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tavsif</label>
                <textarea value={invoiceForm.description} onChange={e => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
                  rows={2} className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setShowModal(null); setError(''); }} className="px-4 py-2 border rounded-lg">Bekor</button>
              <button onClick={handleInvoiceSubmit} disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== TRANSACTION MODAL ===== */}
      {showModal === 'transaction' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-4 md:p-6">
            <h3 className="text-xl font-bold mb-4">{editItem ? "To'lovni tahrirlash" : "Yangi to'lov"}</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            <div className="flex gap-3 mb-4">
              {[{ val: 'income', label: '↑ Kirim' }, { val: 'expense', label: '↓ Chiqim' }].map(t => (
                <button key={t.val} onClick={() => setTransactionForm({ ...transactionForm, transaction_type: t.val })}
                  className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition ${
                    transactionForm.transaction_type === t.val
                      ? t.val === 'income' ? 'border-green-500 bg-green-50 text-green-700' : 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-200'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mijoz</label>
                <select value={transactionForm.client_id} onChange={e => setTransactionForm({ ...transactionForm, client_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_person}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Yetkazib beruvchi</label>
                <select value={transactionForm.supplier_id} onChange={e => setTransactionForm({ ...transactionForm, supplier_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.company_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice</label>
                <select value={transactionForm.invoice_id} onChange={e => setTransactionForm({ ...transactionForm, invoice_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {invoices.map(i => <option key={i.id} value={i.id}>{i.number}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valyuta</label>
                <select value={transactionForm.currency} onChange={e => setTransactionForm({ ...transactionForm, currency: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="usd">USD</option><option value="eur">EUR</option><option value="uzs">UZS</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Summa *</label>
                <input type="number" value={transactionForm.amount} onChange={e => setTransactionForm({ ...transactionForm, amount: Number(e.target.value) })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sana</label>
                <input type="date" value={transactionForm.transaction_date} onChange={e => setTransactionForm({ ...transactionForm, transaction_date: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tavsif</label>
                <input value={transactionForm.description} onChange={e => setTransactionForm({ ...transactionForm, description: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setShowModal(null); setEditItem(null); setError(''); }} className="px-4 py-2 border rounded-lg">Bekor</button>
              <button onClick={handleTransactionSubmit} disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== EXPENSE MODAL ===== */}
      {showModal === 'expense' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-4 md:p-6">
            <h3 className="text-xl font-bold mb-4">{editItem ? 'Xarajatni tahrirlash' : 'Yangi xarajat'}</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Sarlavha *</label>
                <input value={expenseForm.title} onChange={e => setExpenseForm({ ...expenseForm, title: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategoriya</label>
                <select value={expenseForm.category} onChange={e => setExpenseForm({ ...expenseForm, category: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {Object.entries(expenseCategoryConfig).map(([k, v]) =>
                    <option key={k} value={k}>{v.icon} {v.label}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valyuta</label>
                <select value={expenseForm.currency} onChange={e => setExpenseForm({ ...expenseForm, currency: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="uzs">UZS</option><option value="usd">USD</option><option value="eur">EUR</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Summa *</label>
                <input type="number" value={expenseForm.amount} onChange={e => setExpenseForm({ ...expenseForm, amount: Number(e.target.value) })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sana</label>
                <input type="date" value={expenseForm.expense_date} onChange={e => setExpenseForm({ ...expenseForm, expense_date: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tavsif</label>
                <textarea value={expenseForm.description} onChange={e => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  rows={2} className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setShowModal(null); setError(''); }} className="px-4 py-2 border rounded-lg">Bekor</button>
              <button onClick={handleExpenseSubmit} disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SHARTNOMA EXPORT MODAL ===== — Contract Detail modal ustida ochilishi mumkin, shuning uchun yuqoriroq z-index */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-4 md:p-6">
            <h3 className="text-xl font-bold mb-2">Shartnomani yuklab olish</h3>
            <p className="text-gray-500 text-sm mb-5">Til va format tanlang</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Til</label>
              <div className="grid grid-cols-3 gap-2">
                {[{ val: 'uz', label: "🇺🇿 O'zbek" }, { val: 'ru', label: '🇷🇺 Rus' }, { val: 'en', label: '🇬🇧 Ingliz' }]
                  .filter(l => !(l.val === 'en' && exportIsPurchase)).map(l => (
                  <button key={l.val} onClick={() => setExportLang(l.val)}
                    className={`py-2 px-3 rounded-lg border-2 text-xs font-medium transition ${
                      exportLang === l.val ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200'
                    }`}>
                    {l.label}
                  </button>
                ))}
              </div>
              {exportIsPurchase && (
                <p className="text-xs text-gray-500 mt-2">Xarid shartnomasi ikki tilli: tanlangan til chap ustunda, o&apos;ng ustun har doim inglizcha.</p>
              )}
            </div>
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { val: 'pdf',  label: '📄 PDF',        activeColor: 'border-red-500 bg-red-50 text-red-700' },
                  { val: 'docx', label: '📝 Word (DOCX)', activeColor: 'border-blue-500 bg-blue-50 text-blue-700' },
                ].map(f => (
                  <button key={f.val} onClick={() => setExportFormat(f.val)}
                    className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition ${
                      exportFormat === f.val ? f.activeColor : 'border-gray-200'
                    }`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowExportModal(null)} className="px-4 py-2 border rounded-lg text-sm">Bekor</button>
              <button onClick={() => {
                const contract = contracts.find(c => c.id === showExportModal);
                if (contract) handleContractExport(contract.id, contract.number, contract.contract_type);
              }} className={`px-4 py-2 text-white rounded-lg text-sm font-medium ${exportFormat === 'pdf' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                ⬇️ Yuklab olish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== INVOICE DETAIL MODAL ===== */}
      {showInvoiceDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-blue-600 font-bold text-lg">{showInvoiceDetail.number}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${invoiceTypeConfig[showInvoiceDetail.invoice_type]?.color}`}>
                    {invoiceTypeConfig[showInvoiceDetail.invoice_type]?.icon} {invoiceTypeConfig[showInvoiceDetail.invoice_type]?.label}
                  </span>
                </div>
                <p className="text-gray-500 text-sm">{getPartyName(showInvoiceDetail)}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${invoiceStatusConfig[showInvoiceDetail.status]?.color}`}>
                {invoiceStatusConfig[showInvoiceDetail.status]?.label}
              </span>
            </div>

            {/* Summa */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Summa</p>
                <p className="font-bold text-blue-600">{showInvoiceDetail.amount.toLocaleString()} {currencySymbol[showInvoiceDetail.currency]}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">To'landi</p>
                <p className="font-bold text-green-600">{showInvoiceDetail.paid_amount.toLocaleString()} {currencySymbol[showInvoiceDetail.currency]}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Qoldi</p>
                <p className="font-bold text-red-500">{(showInvoiceDetail.amount - showInvoiceDetail.paid_amount).toLocaleString()} {currencySymbol[showInvoiceDetail.currency]}</p>
              </div>
            </div>

            {showInvoiceDetail.description && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm text-gray-600">
                <b>Tavsif:</b> {showInvoiceDetail.description}
              </div>
            )}

            {/* Tovarlar ro'yxati */}
            {showInvoiceDetail.items && showInvoiceDetail.items.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">📦 Tovarlar ro'yxati</p>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-blue-900 text-white">
                      <tr>
                        <th className="px-2 py-2 text-left">#</th>
                        <th className="px-2 py-2 text-left">Nomi</th>
                        <th className="px-2 py-2 text-center">Miqdor</th>
                        <th className="px-2 py-2 text-center">Birlik</th>
                        <th className="px-2 py-2 text-right">Narx</th>
                        <th className="px-2 py-2 text-right">Jami</th>
                      </tr>
                    </thead>
                    <tbody>
                      {showInvoiceDetail.items.map((item, idx) => (
                        <tr key={item.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-2 py-2 text-gray-400">{idx + 1}</td>
                          <td className="px-2 py-2">
                            <div className="font-medium text-gray-800">{item.product_name}</div>
                            {item.product_code && <div className="text-gray-400 font-mono">{item.product_code}</div>}
                          </td>
                          <td className="px-2 py-2 text-center">{item.quantity}</td>
                          <td className="px-2 py-2 text-center text-gray-500">{item.unit || '—'}</td>
                          <td className="px-2 py-2 text-right text-gray-600">{item.unit_price.toLocaleString()}</td>
                          <td className="px-2 py-2 text-right font-bold text-blue-600">{item.total_price.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-blue-50 border-t-2 border-blue-200">
                      <tr>
                        <td colSpan={5} className="px-2 py-2 text-right font-bold text-gray-700">JAMI:</td>
                        <td className="px-2 py-2 text-right font-bold text-blue-700 text-sm">
                          {showInvoiceDetail.items.reduce((s, i) => s + i.total_price, 0).toLocaleString()} {currencySymbol[showInvoiceDetail.currency]}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* PDF Fayl yuklash */}
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">📎 Invoice fayli (PDF)</p>
              {showInvoiceDetail.file_url ? (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
                  <span className="text-green-600">✅ PDF fayl yuklangan</span>
                  <div className="flex gap-2 ml-auto">
                    <button onClick={() => handleFileView(showInvoiceDetail.id)}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs hover:bg-blue-200">
                      👁️ Ko'rish
                    </button>
                    {canFull && (
                      <button onClick={() => handleFileDelete(showInvoiceDetail.id)} title="Faylni o'chirish"
                        className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-xs hover:bg-red-200">
                        🗑️ O'chirish
                      </button>
                    )}
                  </div>
                </div>
              ) : canFull ? (
                <label className={`flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition ${uploadingFile ? 'opacity-50' : ''}`}>
                  <span className="text-lg mb-1">📄</span>
                  <span className="text-xs text-gray-500">
                    {uploadingFile ? 'Yuklanmoqda...' : 'PDF fayl yuklash uchun bosing'}
                  </span>
                  <input type="file" accept=".pdf" className="hidden" disabled={uploadingFile}
                    onChange={e => e.target.files && handleFileUpload(showInvoiceDetail.id, e.target.files[0])} />
                </label>
              ) : (
                <div className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-gray-200 rounded-lg text-gray-400">
                  <span className="text-lg mb-1">📄</span>
                  <span className="text-xs">Fayl yuklanmagan</span>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 flex-wrap">
              <button onClick={async () => {
                const lang = prompt("Til tanlang:\nuz — O'zbek\nru — Rus\nen — Ingliz", "uz");
                if (!lang) return;
                try {
                  const res = await api.get(`/invoices/${showInvoiceDetail.id}/export/pdf`, {
                    params: { lang: lang.trim().toLowerCase() || 'uz' },
                    responseType: 'blob'
                  });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement('a'); a.href = url;
                  a.download = `invoice_${showInvoiceDetail.number}_${lang}.pdf`; a.click();
                  URL.revokeObjectURL(url);
                } catch { alert('PDF yaratishda xato'); }
              }} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
                📄 PDF
              </button>
              <button onClick={async () => {
                try {
                  const res = await api.get(`/invoices/${showInvoiceDetail.id}/export/excel`, { responseType: 'blob' });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement('a'); a.href = url;
                  a.download = `invoice_${showInvoiceDetail.number}.xlsx`; a.click();
                  URL.revokeObjectURL(url);
                } catch { alert('Excel yaratishda xato'); }
              }} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                📊 Excel
              </button>
              {showInvoiceDetail.contract_id && (
                <button onClick={() => { setShowInvoiceDetail(null); setActiveTab('supply'); }}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm">
                  📄 Shartnomaga o'tish
                </button>
              )}
              <button onClick={() => handleCreateTransaction(showInvoiceDetail.id, showInvoiceDetail.number)}
                disabled={showInvoiceDetail.status === 'paid'}
                className={`px-4 py-2 rounded-lg text-sm text-white ${showInvoiceDetail.status === 'paid' ? 'bg-gray-300 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
                💳 To'lov yaratish
              </button>
              <button onClick={() => { setShowInvoiceDetail(null); setActiveTab('transactions'); }}
                className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 text-sm">
                💳 To'lovlarni ko'rish
              </button>
              <button onClick={() => setShowInvoiceDetail(null)} title="Yopish"
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Yopish</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounting;